import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import messageService from '../services/messageService';
import {
  DeleteOutline as DeleteIcon,
  Send as SendIcon,
  MailOutline as MailIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import ComposeMessageModal from '../components/messages/ComposeMessageModal';
import useStore from '../store/useStore';

const Messages = () => {
  const { user, toast } = useStore();
  const [activeTab, setActiveTab] = useState('inbox'); // 'inbox' or 'sent'
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const canSendMessages = Boolean(user?.role && user.role !== 'Staff');
  const canViewSent = Boolean(user?.role && user.role !== 'Staff');

  // Fetch messages based on active tab
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = activeTab === 'inbox'
        ? await messageService.getInbox()
        : await messageService.getSentMessages();
      setMessages(res.data.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await messageService.getUnreadCount();
      setUnreadCount(res.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    if (!canViewSent && activeTab === 'sent') {
      setActiveTab('inbox');
    }
    fetchMessages();
    fetchUnreadCount();
  }, [activeTab, canViewSent, fetchMessages, fetchUnreadCount]);

  // Mark message as read
  const handleMarkAsRead = async (messageId) => {
    try {
      await messageService.markAsRead(messageId);
      setMessages((prev) => prev.map((msg) => (
        msg.id === messageId ? { ...msg, isRead: true } : msg
      )));
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messageService.deleteMessage(messageId);
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        setSelectedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
        toast('Message deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting message:', error);
        toast(error.response?.data?.message || 'Failed to delete message', 'error');
      }
    }
  };

  // Toggle message selection
  const handleToggleSelect = (messageId) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Delete selected messages
  const handleDeleteSelected = async () => {
    if (selectedMessages.size === 0) return;
    
    if (window.confirm(`Delete ${selectedMessages.size} message(s)?`)) {
      try {
        await Promise.all(
          Array.from(selectedMessages).map((id) => messageService.deleteMessage(id))
        );
        setMessages((prev) => prev.filter((msg) => !selectedMessages.has(msg.id)));
        const deletedCount = selectedMessages.size;
        setSelectedMessages(new Set());
        toast(`${deletedCount} message(s) deleted successfully`, 'success');
      } catch (error) {
        console.error('Error deleting messages:', error);
        toast(error.response?.data?.message || 'Failed to delete selected messages', 'error');
      }
    }
  };

  // Handle message sent
  const handleMessageSent = () => {
    setShowCompose(false);
    fetchMessages();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <PageHeader title="Messages" />
        {canSendMessages && (
          <Button onClick={() => setShowCompose(true)} icon={SendIcon}>
            Compose Message
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('inbox')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'inbox'
              ? 'text-teal-700 border-b-2 border-teal-700'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Inbox {unreadCount > 0 && activeTab === 'inbox' && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
        {canViewSent && (
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'sent'
                ? 'text-teal-700 border-b-2 border-teal-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sent
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedMessages.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
          <span className="text-blue-900">
            {selectedMessages.size} message(s) selected
          </span>
          <button
            onClick={handleDeleteSelected}
            className="text-red-600 hover:text-red-900 flex items-center gap-2"
          >
            <DeleteIcon fontSize="small" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Messages List */}
      <div className="bg-white rounded-lg border border-gray-200 divide-y">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MailIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`p-4 hover:bg-gray-50 transition cursor-pointer flex items-center gap-4 ${
                !msg.isRead && activeTab === 'inbox' ? 'bg-blue-50' : ''
              }`}
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedMessages.has(msg.id)}
                onChange={() => handleToggleSelect(msg.id)}
                className="w-4 h-4 cursor-pointer"
              />

              {/* Message Info */}
              <div
                className="flex-1 min-w-0"
                onClick={() => !msg.isRead && activeTab === 'inbox' && handleMarkAsRead(msg.id)}
              >
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className={`font-medium text-gray-900 ${!msg.isRead ? 'font-bold' : ''}`}>
                      {activeTab === 'inbox' ? msg.senderName : msg.recipientName}
                    </p>
                    <p className={`text-sm truncate ${
                      !msg.isRead ? 'text-gray-900 font-medium' : 'text-gray-600'
                    }`}>
                      {msg.subject}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{msg.message.substring(0, 100)}</p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(msg.createdAt)}
                  </span>
                </div>
              </div>

              {/* Status Indicator */}
              {activeTab === 'inbox' && !msg.isRead && (
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {activeTab === 'inbox' && !msg.isRead && (
                  <button
                    onClick={() => handleMarkAsRead(msg.id)}
                    title="Mark as read"
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <CheckIcon fontSize="small" className="text-gray-600" />
                  </button>
                )}
                <button
                  onClick={() => handleDeleteMessage(msg.id)}
                  title="Delete"
                  className="p-1 hover:bg-red-100 rounded"
                >
                  <DeleteIcon fontSize="small" className="text-red-600" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && canSendMessages && (
        <ComposeMessageModal
          onClose={() => setShowCompose(false)}
          onSent={handleMessageSent}
        />
      )}
    </div>
  );
};

export default Messages;
